import axios from "axios";

const DASHBOARD_API_URL = process.env.REACT_APP_REPORT_URL;

// Bearer-header pattern matches src/services/adminCommonService.js:
// Authorization is the raw id_token string (no "Bearer " prefix added here),
// orgId is a plain lowercase header key.
const adminHeaders = (orgId) => ({
  Authorization: localStorage.getItem("id_token"),
  orgId,
});

// ── Access revocation signalling ───────────────────────────────────────────
// Every dashboard request below funnels through `guard`. The backend already
// re-checks the viewing grant on each call — DashboardPublicController answers
// 404 from ValidateToken on every action once a share token is revoked, and
// CustomAuthorizationFilter answers 401/403 once an admin session lapses — but
// those rejections used to die inside usePaginatedList's unread `error` state
// or in an uncaught promise for the drill-down fetches. A revoked viewer kept
// browsing stale data until they hard-refreshed. `guard` turns any of them into
// one broadcast the dashboard pages listen for.
const accessRevokedSubscribers = new Set();

export function subscribeAccessRevoked(handler) {
  accessRevokedSubscribers.add(handler);
  return () => {
    accessRevokedSubscribers.delete(handler);
  };
}

// Admin 404 is deliberately NOT treated as a revoked grant: GetOrgAll answers
// 404 for "Organisation not found", a data condition, not an access one.
function isAccessRevoked(err, mode) {
  const status = err?.response?.status;
  if (mode === "public") return status === 404;
  return status === 401 || status === 403;
}

async function guard(mode, request) {
  try {
    return await request();
  } catch (err) {
    if (isAccessRevoked(err, mode)) {
      accessRevokedSubscribers.forEach((handler) => handler({ mode, status: err.response.status }));
    }
    throw err;
  }
}

// Public, unauthenticated — used by the shareable /d/:token dashboard page.
export async function fetchPublicInfo(token) {
  return guard("public", async () => {
    const res = await axios.get(`${DASHBOARD_API_URL}/DashboardPublic/Info`, { params: { token } });
    return res.data;
  });
}

export async function fetchPublicAll(token, { from, to } = {}) {
  return guard("public", async () => {
    const res = await axios.get(`${DASHBOARD_API_URL}/DashboardPublic/All`, {
      params: { token, from: from || undefined, to: to || undefined },
    });
    return res.data;
  });
}

export async function fetchAdminOrgAll(orgId, { from, to } = {}) {
  return guard("admin", async () => {
    const res = await axios.get(`${DASHBOARD_API_URL}/DashboardAdmin/GetOrgAll`, {
      headers: adminHeaders(orgId),
      params: { orgId, from: from || undefined, to: to || undefined },
    });
    return res.data;
  });
}

export async function createShareToken({ organisationId, clientName, accentColor, expiresAt, createdBy }) {
  const res = await axios.post(
    `${DASHBOARD_API_URL}/Token/Create`,
    { organisationId, clientName, accentColor, expiresAt, createdBy },
    { headers: adminHeaders(organisationId) }
  );
  return res.data;
}

export async function fetchTokens(orgId) {
  const res = await axios.get(`${DASHBOARD_API_URL}/Token/List`, {
    headers: adminHeaders(orgId),
    params: { orgId },
  });
  return res.data;
}

export async function revokeToken(token, orgId) {
  await axios.delete(`${DASHBOARD_API_URL}/Token/Revoke`, {
    headers: adminHeaders(orgId),
    params: { token },
  });
}

function buildListFetcher(adminAction, publicAction) {
  return (apiCtx, params = {}) => {
    const query = { from: apiCtx.from || undefined, to: apiCtx.to || undefined, ...params };
    if (apiCtx.mode === "public") {
      return guard("public", () =>
        axios
          .get(`${DASHBOARD_API_URL}/DashboardPublic/${publicAction}`, { params: { token: apiCtx.token, ...query } })
          .then((res) => res.data)
      );
    }
    return guard("admin", () =>
      axios
        .get(`${DASHBOARD_API_URL}/DashboardAdmin/${adminAction}`, {
          headers: adminHeaders(apiCtx.orgId),
          params: { orgId: apiCtx.orgId, ...query },
        })
        .then((res) => res.data)
    );
  };
}

export const fetchCases = buildListFetcher("GetCasesList", "CasesList");
export const fetchReportItems = buildListFetcher("GetReportItemsList", "ReportItemsList");
export const fetchCredentials = buildListFetcher("GetCredentialsList", "CredentialsList");
export const fetchProjects = buildListFetcher("GetProjectsList", "ProjectsList");
export const fetchObservations = buildListFetcher("GetObservationsList", "ObservationsList");
export const fetchReprimands = buildListFetcher("GetReprimandsList", "ReprimandsList");
export const fetchIncidents = buildListFetcher("GetIncidentsList", "IncidentsList");
// Currently unused. GetCorrectiveItemsList is backed by InsnCorrective, which holds only
// inspection-corrective rows and so returns a smaller population than the dashboard's own
// Corrective counts (which come from the findings log, same as /openitemlist). Prefer
// fetchReportItems with { itemType: "Corrective" } for any new corrective drill, or this
// list will disagree with the number the user clicked.
export const fetchCorrectiveItems = buildListFetcher("GetCorrectiveItemsList", "CorrectiveItemsList");
export const fetchVehicles = buildListFetcher("GetVehiclesList", "VehiclesList");
export const fetchEquipment = buildListFetcher("GetEquipmentList", "EquipmentList");
export const fetchCustomAssets = buildListFetcher("GetCustomAssetsList", "CustomAssetsList");
export const fetchUsers = buildListFetcher("GetUsersList", "UsersList");
export const fetchLmsAssignments = buildListFetcher("GetLmsAssignmentsList", "LmsAssignmentsList");
export const fetchSafetyMeetings = buildListFetcher("GetSafetyMeetingsList", "SafetyMeetingsList");
